import { NgClass, NgStyle } from "@angular/common";
import { AfterViewInit, ChangeDetectorRef, Component, DoCheck, EventEmitter, inject, Input, input, OnDestroy, OnInit, Output, signal, WritableSignal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AbstractControl, ControlContainer, FormsModule, NgModelGroup } from "@angular/forms";
import { ClassDirective, ShowHideDirective, StyleDirective } from "@ngbracket/ngx-layout/extended";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ErrorMessage, Utilities } from "@tylertech/shared-module";
import { Align, ClassMetadata, PropertyMetadata } from "@tylertech/tyler-api-types";
import { filter } from "rxjs/operators";
import { AugmentedModelObject, AugmentedPropertyMetadata, ModelChangeArg, ModifierCommandArgs, RepositoryFormData, ValueChangedParms } from "../../interfaces";
import { BreadcrumbArg, CellEditService, MetadataErrorService, MetadataEventService, MetadataInputFormatterService, MetadataInputSelectService, MetadataInputTypeHelperService, MetadataPropertyService, MetadataService, MetadataValidationService, PROPERTY_CONTROLS } from "../../services";
import { ArrayHelpers } from "../../utilities";
import { MetadataCalendarComponent } from "../metadataCalendar/metadataCalendar.component";
import { MetadataCalendarMonthComponent } from "../metadataCalendar/metadataCalendarMonth.component";
import { MetadataChartComponent } from "../metadataChart/metadataChart.component";
import { MetadataPhotoComponent } from "../metadataPhoto/metadataPhoto.component";
import { MetadataSpatialMapComponent } from "../metadataSpatialMap/metadataSpatialMap.component";
import { MetadataTreeComponent } from "../metadataTree/metadataTree/metadata.tree.component";
import { MetadataGenericButtonComponent } from "./controls/actionable/metadata-generic-button.component";
import { MetadataIconButtonComponent } from "./controls/actionable/metadata-icon-button.component";
import { MetadataLinkComponent } from "./controls/actionable/metadata-link.component";
import { MetadataTextButtonComponent } from "./controls/actionable/metadata-text-button.component";
import { MetadataBadgeComponent } from "./controls/badge/metadata-badge.component";
import { MetadataInputCheckboxComponent } from "./controls/checkbox/metadata-input-checkbox.component";
import { MetadataInputColorComponent } from "./controls/color/metadata-input-color.component";
import { MetadataInputDateComponent } from "./controls/date/metadata-input-date.component";
import { MetadataInputDateTimeComponent } from "./controls/datetime/metadata-input-date-time.component";
import { MetadataInputFileComponent } from "./controls/file/metadata-input-file.component";
import { MetadataHtmlComponent } from "./controls/html/metadataHtml.component";
import { MetadataInputInlineMessageComponent } from "./controls/inlineMessage/metadata-inline-message.component";
import { MetadataHeaderComponent } from "./controls/label/metadata-header.component";
import { MetadataLabelComponent } from "./controls/label/metadata-label.component";
import { MetadataInputLabelValueComponent } from "./controls/labelValue/metadata-label-value.component";
import { MetadataInputNumberComponent } from "./controls/number/metadata-input-number.component";
import { MetadataInputPhoneNumberComponent } from "./controls/phoneNumber/metadata-input-phone-number.component";
import { MetadataRichTextComponent } from "./controls/richText/metadata-rich-text.component";
import { MetadataScriptComponent } from "./controls/script/metadataScript.component";
import { MetadataInputDualListComponent } from "./controls/select/metadata-input-dual-list.component";
import { MetadataInputMultiSelectComponent } from "./controls/select/metadata-input-multi-select.component";
import { MetadataInputTextComponent } from "./controls/text/metadata-input-text.component";
import { MetadataInputTextAreaComponent } from "./controls/text/metadata-input-textarea.component";
import { MetadataInputTimeComponent } from "./controls/time/metadata-input-time.component";
import { MetadataInputTypeaheadComponent } from "./controls/typeahead/metadata-input-typeahead.component";

@Component({
    selector: "metadata-input",
    templateUrl: "./metadataInput.tmpl.html",
    viewProviders: [{
        provide: ControlContainer,
        useExisting: NgModelGroup
    }],
    imports: [
        ClassDirective,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ShowHideDirective,
        StyleDirective,
        MetadataBadgeComponent,
        MetadataCalendarComponent,
        MetadataCalendarMonthComponent,
        MetadataChartComponent,
        MetadataGenericButtonComponent,
        MetadataHeaderComponent,
        MetadataHtmlComponent,
        MetadataIconButtonComponent,
        MetadataInputCheckboxComponent,
        MetadataInputColorComponent,
        MetadataInputDateComponent,
        MetadataInputDateTimeComponent,
        MetadataInputDualListComponent,
        MetadataInputFileComponent,
        MetadataInputInlineMessageComponent,
        MetadataInputLabelValueComponent,
        MetadataInputMultiSelectComponent,
        MetadataInputNumberComponent,
        MetadataInputPhoneNumberComponent,
        MetadataInputTextAreaComponent,
        MetadataInputTextComponent,
        MetadataInputTimeComponent,
        MetadataInputTypeaheadComponent,
        MetadataLabelComponent,
        MetadataLinkComponent,
        MetadataPhotoComponent,
        MetadataRichTextComponent,
        MetadataScriptComponent,
        MetadataSpatialMapComponent,
        MetadataTextButtonComponent,
        MetadataTreeComponent,
        FormsModule, //import module here because NgModelGroup is not standalone
        NgClass,
        NgStyle,
    ]
})
export class MetadataInputComponent implements OnInit, OnDestroy, AfterViewInit, DoCheck
{
    modelGroup = inject(NgModelGroup);
    cdr = inject(ChangeDetectorRef);
    private $metadataService = inject(MetadataService);
    private $metadataPropertyService = inject(MetadataPropertyService);
    private $metadataValidationService = inject(MetadataValidationService);
    private $metadataInputTypeHelperService = inject(MetadataInputTypeHelperService);
    private readonly $metadataInputSelectService = inject(MetadataInputSelectService);
    private $metadataEventService = inject(MetadataEventService);
    private readonly $metadataErrorService = inject(MetadataErrorService);
    private readonly $metadataInputFormatterService = inject(MetadataInputFormatterService);
    private readonly $cellEditService = inject(CellEditService);

    readonly index = input<number>();

    // REVIEW - how difficult would it be to make this binding be the propertyMetadata, instead of the field name?
    readonly field = input.required<string>();

    @Input()
    metadataModel: AugmentedModelObject;

    @Input()
    parent: AugmentedModelObject;

    @Input()
    metadata: ClassMetadata;

    /**
     * NOTE: Can't name it `dataController`, it seems that angularJs eats data-controller attributes somehow.
     * I can't find it in the angularJs source code, but when changed to `dataController`,
     * the binding is never resolved.
     */
    @Input()
    datacontroller?: RepositoryFormData;

    readonly viewOnly = input<boolean>();

    readonly dense = input<boolean>();

    readonly forCellEdit = input<boolean>();

    readonly parentGridProvider = input<string>();

    readonly isMenu = input<boolean>();

    readonly compact = input<boolean>();
    readonly onPanelGrid = input<boolean>();

    /**
     * TODO - this binding really needs some documentation about it's intended usage and purpose.
     *
     * This method is fired AFTER the field this `metadataInput` represents is changed on the `metadataModel`.
     */
    @Output()
    modifierCallback = new EventEmitter<ModifierCommandArgs>();

    inputType: string;
    delayRecalculateForCellEdit: boolean;
    includeContainer: boolean;
    isHeader: boolean;
    isLabel: boolean;
    isLabelValue: boolean;
    isRichText: boolean;
    isCheck: boolean;
    isNumeric: boolean;
    isCurrency: boolean;
    isSpatialMap: boolean;
    isCalendar: boolean;
    isMonthCalendar: boolean;
    isLink: boolean;
    isChart: boolean;
    isBadge: boolean;
    isTree: boolean;
    isInlineMessage: boolean;
    isScript: boolean;
    isHtml: boolean;
    isFrontIconButton: boolean;
    isFrontTextButton: boolean;
    isBackIconButton: boolean;
    isBackTextButton: boolean;
    containerClasses: Record<string, boolean> = {};

    isHidden: boolean;
    Label: string;
    formattedModel: any;
    allowAdd: boolean;
    allowEdit: boolean;
    precision: number;
    scale: number;
    minValue: number;
    maxValue: number;
    Hint: string;

    private controlId: string;

    readonly inputGroupName = `inputGrp_${Utilities.generateRandomGuid()}`;

    get control(): AbstractControl
    {
        return this.modelGroup?.control?.get([this.inputGroupName, this.propertyMetadata.Name]);
    }

    private isInitialized: boolean = false;

    propertyMetadata: AugmentedPropertyMetadata;

    private _isInitialized: boolean;

    private readonly _errorMessages: ErrorMessage[]; // readonly so we don't constantly reinitialize the field and trigger $digest loops when the identity changes

    private readonly _errorMessageSignal: WritableSignal<string> = signal('');
    private _previousControlErrors: Record<string, any> | null = null;

    private recomputeQueued: boolean = false;

    constructor()
    {
        this._errorMessages = [];

        this.$metadataService.onProxyPropertyChanged.pipe(takeUntilDestroyed(), filter((arg: ModelChangeArg) => this.$metadataService.compareModels(arg.Model, this.metadataModel)))
            .subscribe(() => this.queueComputeProperties());
    }

    ngOnInit(): void
    {
        this.isInitialized = true;
        if (this.onPanelGrid())
            this.controlId = Utilities.generateRandomGuid();
        this.checkMetadata(true);
        const self = this;
        if (this.propertyMetadata.NoFlagForSave)
        {
            setTimeout(() =>
            {
                self.control.markAsPristine();
            });
        }
        if (this.forCellEdit())
        {
            if (this.metadataModel["$cellEditInvalidProperties"])
            {
                const index = this.metadataModel["$cellEditInvalidProperties"].indexOf(this.propertyMetadata.Name);
                if (index > -1)
                    this.metadataModel["$cellEditInvalidProperties"].splice(index, 1);
            }
            if (this.metadataModel.$errors)
                delete this.metadataModel.$errors[this.propertyMetadata.Name];
        }

        if (this.propertyMetadata.MinValueProperty && !this.metadata.Properties.some(p => p.Name === this.propertyMetadata.MinValueProperty))
            this.propertyMetadata["$listenForNonPropertyChange"] = true;
        if (this.propertyMetadata.MaxValueProperty && !this.metadata.Properties.some(p => p.Name === this.propertyMetadata.MaxValueProperty))
            this.propertyMetadata["$listenForNonPropertyChange"] = true;

        this.containerClasses = {
            "right-align-float-label": this.isNumeric,
            "date-and-or-time-input":
                this.$metadataInputTypeHelperService.isDate(this.inputType) ||
                this.$metadataInputTypeHelperService.isDateTime(this.inputType) ||
                this.$metadataInputTypeHelperService.isTime(this.inputType) ||
                this.$metadataInputTypeHelperService.isTime24(this.inputType),
            "dense-input": this.dense()
        };

        this.computeProperties();
    }

    ngDoCheck(): void
    {
        if (this.forCellEdit())
            this.$cellEditService.SetCellEditValidity(this.parentGridProvider(), this.metadataModel, this.propertyMetadata.Name, this.modelGroup?.invalid);

        if (!this._isInitialized)
            return;

        const currentErrors = this.control?.errors;
        if (this.hasErrorsChanged(currentErrors))
        {
            this.$metadataErrorService.consolidateErrors(
                this.propertyMetadata.Name,
                this.metadataModel,
                this.errorMessages
            );
            this._previousControlErrors = currentErrors ? { ...currentErrors } : null;
            this.updateErrorMessage();
        }
    }

    private hasErrorsChanged(currentErrors: Record<string, any> | null | undefined): boolean
    {
        if (!currentErrors && !this._previousControlErrors)
            return false;
        if ((!currentErrors && this._previousControlErrors) || (currentErrors && !this._previousControlErrors))
            return true;
        return !Utilities.objectsEqual(currentErrors || {}, this._previousControlErrors || {});
    }

    /**
     * This is the field on the metadataModel object!
     */
    get model(): any
    {
        return this.metadataModel[this.field()];
    }

    set model(value: any)
    {
        const self = this;
        const fieldValue = this.field();
        const modelObj = this.metadataModel;
        let old = modelObj[fieldValue];
        //don't fire callback twice for same change
        let changed = false;
        if (typeof value === "string")
        {
            if (!Utilities.IsNullOrUndefined(old))
            {
                old = old.toString();
            }
            changed = (Utilities.IsNullOrUndefined(value) ? value : value.toString()) !== old;
        }
        else
        {
            if (!Utilities.IsNullOrUndefined(value) && !Utilities.IsNullOrUndefined(value.FileName))
            {
                changed = value.FileName !== (Utilities.IsNullOrUndefined(old) ? null : old.FileName);
            }
            else
            {
                changed = false;
            }
        }
        changed = (Utilities.IsNullOrUndefined(value) ? value : value.toString()) !== old;
        if ((changed || this.propertyMetadata["$$forceRecalculate"]) && !this.propertyMetadata["$$skipRecalculate"])
        {
            if (this.forCellEdit())
                this.metadataModel["$pristine"] = false;
            delete this.propertyMetadata["$$forceRecalculate"];
            this.$metadataEventService.fireSearchFormBreadcrumbListener(
                <BreadcrumbArg>{
                    Field: fieldValue,
                    Value: value
                });
            // Firing recalc before setting the value so that the recalc param still has the previous value.
            if (!this.isFrontTextButton && !this.isBackTextButton && !this.isFrontIconButton && !this.isBackIconButton && !!modelObj.SerializedRowState && modelObj.SerializedRowState === "Unchanged")
                modelObj.SerializedRowState = "Modified";
            this.fireRecalculate(value)
                .then(
                    _x =>
                    {
                        afterRecalculate();
                    },
                    () =>
                    {
                        //Recalculate can throw legitimate errors (ex. VerificationQuestions) but the model value did change, so we still need to finish what it updates after
                        afterRecalculate();
                    }
                );
        }
        delete this.propertyMetadata["$$skipRecalculate"];
        modelObj[fieldValue] = value;
        function afterRecalculate(): void
        {
            if (self.propertyMetadata.RecalculateOnScan === true)
                delete self.metadataModel["$triggeredByScan"];
            self.fireMetadataModifier(self.metadataModel[fieldValue]);
            self.$metadataInputSelectService.AddOrUpdateOption(
                self.metadata,
                self.metadataModel,
                self.metadata.TypeName,
                self.propertyMetadata,
                value
            );
        }
    }

    private fireRecalculate(newValue: any): Promise<any>
    {
        if (!this.isInitialized || !this.propertyMetadata.RecalculateOnChange)
        {
            return Promise.resolve();
        }
        if (this.forCellEdit())
        {
            if (this.delayRecalculateForCellEdit) return Promise.resolve();
        }
        return this.$metadataPropertyService.recalculateProperties(
            this.metadata.TypeName,
            this.propertyMetadata,
            this.metadataModel,
            this.parent,
            newValue,
            undefined,
            true,
            false,
            this.datacontroller
        );
    }

    private fireMetadataModifier(newValue: any): void
    {
        if (!this.isInitialized)
        {
            return;
        }
        this.modifierCallback.emit(<ModifierCommandArgs>{
            propertyChanged: this.field(),
            propertyValue: newValue,
        });
    }

    setInputTypes(): void
    {
        this.inputType = this.propertyMetadata.InputType;
        this.delayRecalculateForCellEdit = this.$metadataInputTypeHelperService.delayRecalculateForCellEdit(this.inputType);
        this.includeContainer = this.$metadataInputTypeHelperService.includeContainer(this.inputType);
        this.isHeader = this.$metadataInputTypeHelperService.isHeader(this.inputType);
        this.isLabel = this.$metadataInputTypeHelperService.isLabel(this.inputType);
        this.isLabelValue = this.$metadataInputTypeHelperService.isLabelValue(this.inputType);
        this.isRichText = this.$metadataInputTypeHelperService.isRichText(this.inputType);
        this.isCheck = this.$metadataInputTypeHelperService.isCheck(this.inputType);
        this.isCurrency = this.$metadataInputTypeHelperService.isCurrency(this.inputType);
        this.isNumeric = this.$metadataInputTypeHelperService.isNumeric(this.inputType) || this.isCurrency;
        this.isSpatialMap = this.$metadataInputTypeHelperService.isSpatialMap(this.inputType);
        this.isCalendar = this.$metadataInputTypeHelperService.isCalendar(this.inputType);
        this.isMonthCalendar = this.$metadataInputTypeHelperService.isMonthCalendar(this.inputType);
        this.isLink = this.$metadataInputTypeHelperService.isLink(this.inputType);
        this.isChart = this.$metadataInputTypeHelperService.isChart(this.inputType);
        this.isBadge = this.$metadataInputTypeHelperService.isBadge(this.inputType);
        this.isTree = this.$metadataInputTypeHelperService.isTree(this.inputType);
        this.isInlineMessage = this.$metadataInputTypeHelperService.isInlineMessage(this.inputType);
        this.isScript = this.$metadataInputTypeHelperService.isScript(this.inputType);
        this.isHtml = this.$metadataInputTypeHelperService.isHtml(this.inputType);
    }

    queueComputeProperties(): void
    {
        if (this.recomputeQueued)
            return;
        this.recomputeQueued = true;
        requestAnimationFrame(() =>
        {
            this.recomputeQueued = false;
            this.computeProperties();
        });
    }

    computeProperties(): void
    {
        this.isHidden = this.getIsHidden();
        this.Label = this.getLabel();
        this.formattedModel = this.getFormattedModel();
        this.allowAdd = this.getAllowAdd();
        this.allowEdit = this.getAllowEdit();
        this.precision = this.getPrecision();
        this.scale = this.getScale();
        this.minValue = this.getMinValue();
        this.maxValue = this.getMaxValue();
        this.Hint = this.getHint();
    }

    getLabel(): string
    {
        return this.$metadataService.EvaluateString(this.propertyMetadata.Label, this.parent, this.metadataModel);
    }

    getIsHidden(): boolean
    {
        return this.$metadataPropertyService.isHidden(this.propertyMetadata, this.metadataModel, this.parent);
    }

    getFormattedModel(): any
    {
        return this.$metadataInputFormatterService.ApplyFormatting(this.propertyMetadata, this.model, this.metadataModel, this.parent);
    }

    getAllowAdd(): boolean
    {
        return this.$metadataPropertyService.isAllowAdd(this.propertyMetadata, this.metadataModel, this.parent);
    }

    getAllowEdit(): boolean
    {
        return this.$metadataPropertyService.isAllowEdit(this.propertyMetadata, this.metadataModel, this.parent);
    }

    get IconClass(): string
    {
        return this.isCheck ? "checkbox-icon" : "standard-icon";
    }

    setButtons(): void
    {
        this.isFrontIconButton = !!this.propertyMetadata.ActionMetadataFront && !!this.propertyMetadata.ActionMetadataFront.Icon;
        this.isFrontTextButton = !!this.propertyMetadata.ActionMetadataFront && !this.propertyMetadata.ActionMetadataFront.Icon;
        this.isBackIconButton = (!!this.propertyMetadata.ButtonMetadata && !!this.propertyMetadata.ButtonMetadata.ButtonIcon)
            || (!!this.propertyMetadata.ActionMetadata && !!this.propertyMetadata.ActionMetadata.Icon);
        this.isBackTextButton = this.propertyMetadata.ButtonMetadata
            ? !this.propertyMetadata.ButtonMetadata.ButtonIcon
            : this.propertyMetadata.ActionMetadata
                ? !this.propertyMetadata.ActionMetadata.Icon
                : this.$metadataInputTypeHelperService.isButton(this.propertyMetadata.InputType);
        this.align = this.propertyMetadata && this.propertyMetadata.Alignment ? this.propertyMetadata.Alignment : Align.Left;
        this.hasSpacer = this.propertyMetadata && !!this.propertyMetadata.Spacer && this.propertyMetadata.Spacer > 0;
    }

    getIsBackTextButton(): boolean
    {
        if (this.propertyMetadata.ButtonMetadata)
        {
            return !this.propertyMetadata.ButtonMetadata.ButtonIcon;
        }
        if (this.propertyMetadata.ActionMetadata)
        {
            return !this.propertyMetadata.ActionMetadata.Icon;
        }
        return this.$metadataInputTypeHelperService.isButton(this.propertyMetadata.InputType);
    }

    align: Align;

    hasSpacer: boolean;

    get alignLeft(): boolean
    {
        return this.align === Align.Left;
    } // 0

    get alignCenter(): boolean
    {
        return this.align === Align.Center;
    } // 1

    get alignRight(): boolean
    {
        return this.align === Align.Right;
    } // 2

    get hasLeftSpacer(): boolean
    {
        return this.hasSpacer && (this.alignCenter || this.alignRight);
    }

    get hasRightSpacer(): boolean
    {
        return this.hasSpacer && (this.alignLeft || this.alignCenter);
    }

    get spacerWidth(): string
    {
        //round to nearest multiple of 5.
        if (this.hasSpacer)
        {
            const spacer =
                (this.propertyMetadata.Spacer / (this.propertyMetadata.Spacer + this.propertyMetadata.DrawWidth)) * 100;
            if (this.alignCenter)
            {
                return `${spacer / 2}%`;
            }
            else
            {
                return `${spacer}%`;
            }
        }
        else return "0%";
    }

    get alignment(): string
    {
        // if (this.hasSpacer) return "left";
        return this.alignRight ? "right" : this.alignCenter ? "center" : "left";
    }

    get width(): string
    {
        return "100%";
        // const defaultWidth = "auto";
        // if (this.hasSpacer) return defaultWidth;
        // return this.alignRight || this.alignCenter ? "100%" : defaultWidth;
    }

    getPrecision(): number
    {
        return this.propertyMetadata.Precision
            ? this.propertyMetadata.Precision
            : !this.propertyMetadata.PrecisionProperty
                ? 0
                : this.$metadataService.DependentModuleValue(
                    this.propertyMetadata.PrecisionProperty,
                    this.parent,
                    this.metadataModel
                ) || 0;
    }

    getScale(): number
    {
        return this.propertyMetadata.Scale
            ? this.propertyMetadata.Scale
            : !this.propertyMetadata.ScaleProperty
                ? null
                : this.$metadataService.DependentModuleValue(
                    this.propertyMetadata.ScaleProperty,
                    this.parent,
                    this.metadataModel
                );
    }

    getMinValue(): number
    {
        if (this.propertyMetadata.MinValue === 0)
            return 0;
        return this.propertyMetadata.MinValue
            ? this.propertyMetadata.MinValue
            : !this.propertyMetadata.MinValueProperty
                ? null
                : this.$metadataService.DependentModuleValue(
                    this.propertyMetadata.MinValueProperty,
                    this.parent,
                    this.metadataModel
                );
    }

    getMaxValue(): number
    {
        if (this.propertyMetadata.MaxValue === 0)
            return 0;
        return this.propertyMetadata.MaxValue
            ? this.propertyMetadata.MaxValue
            : !this.propertyMetadata.MaxValueProperty
                ? null
                : this.$metadataService.DependentModuleValue(
                    this.propertyMetadata.MaxValueProperty,
                    this.parent,
                    this.metadataModel
                );
    }

    get hasHint(): boolean
    {
        return !!this.propertyMetadata && !!this.Hint;
    }

    getHint(): string
    {
        return this.propertyMetadata.Hint
            ? this.propertyMetadata.Hint
            : !this.propertyMetadata.HintProperty
                ? null
                : this.$metadataService.DependentModuleValue(
                    this.propertyMetadata.HintProperty,
                    this.parent,
                    this.metadataModel
                );
    }

    get MaxLength(): number
    {
        return this.$metadataInputTypeHelperService.getMaxLength(this.propertyMetadata, this.metadataModel, this.parent);
    }

    get isFullWidth(): boolean
    {
        return this.propertyMetadata?.FullWidth;
    }

    private checkMetadata(initialCheck: boolean): void
    {
        const self = this;
        const metadataValue = this.metadata;
        if (!metadataValue)
        {
            console.error("No metadata found to display");
        }
        else
        {
            if (
                initialCheck &&
                !Utilities.IsNullOrUndefined(metadataValue) &&
                !Utilities.IsNullOrUndefined(metadataValue.Properties) &&
                !Utilities.IsNullOrUndefined(this.field())
            )
            {
                this.propertyMetadata = findMetadataProperties();
                this.setInputTypes();
                this.setButtons();
            }
        }
        function findMetadataProperties(): PropertyMetadata
        {
            const fieldName = self.field().toLowerCase();
            return ArrayHelpers.first(metadataValue.Properties, value => value.Name.toLowerCase() === fieldName);
        }
    }

    onValueChanged($event: ValueChangedParms): void
    {
        if (!$event.validateOnly)
        {
            if (this.model === $event.value && !this.propertyMetadata["$$forceRecalculate"])
                return;

            if (this.propertyMetadata.RecalculateOnScan === true && $event.triggeredByScan === true)
                this.metadataModel["$triggeredByScan"] = true;
            this.model = $event.value;
        }
        if (!this.control)
        {
            return;
        }
        this.$metadataValidationService.validate(
            this.control,
            this.metadata,
            this.propertyMetadata,
            this.metadataModel,
            this.parent
        );
        this.control.markAsDirty();
        if (this.control.valid)
        {
            //No errors
            if (!Utilities.IsNullOrUndefined(this.propertyMetadata.CompareType))
                this.control.setErrors(null);
        }
        else
            this.$metadataErrorService.clearError(this.control, this.metadataModel, this.propertyMetadata.Name);

        this.updateErrorMessage();

        if ($event.cd)
            this.cdr.detectChanges();
    }

    get showSpinner(): boolean
    {
        if (!this.$metadataPropertyService.isBeingRecalculated(this.propertyMetadata, this.metadataModel))
        {
            this.allowRecalculateSpinner = false;
            return false;
        }
        return this.allowRecalculateSpinner;
    }

    allowRecalculateSpinner: boolean;

    onKeydown($event: any): void
    {
        if ($event.code !== "Tab")
        {
            if (this.$metadataPropertyService.isBeingRecalculated(this.propertyMetadata, this.metadataModel))
            {
                this.allowRecalculateSpinner = true;
                $event.preventDefault();
                $event.stopPropagation();
                return;
            }
        }
        this.allowRecalculateSpinner = false;

        if (this.forCellEdit() && $event.code === "Escape")
            this.$cellEditService.CellEditCancel[this.parentGridProvider()] = true;
    }

    ngAfterViewInit(): void
    {
        if (this._isInitialized)
            return;
        requestAnimationFrame(() => this.$metadataValidationService.updateRelatedProperties(this.metadata, this.propertyMetadata, this.control));
        this._isInitialized = true;
    }

    ngOnDestroy(): void
    {
        if (this.metadata[PROPERTY_CONTROLS])
            delete this.metadata[PROPERTY_CONTROLS];
        if (this.onPanelGrid())
        {
            delete this.propertyMetadata.$$formControlError; //delete base error, if another row has it, it'll be in the controlId dictionary
            if (this.propertyMetadata.$panelGridControlErrors)
                delete this.propertyMetadata.$panelGridControlErrors[this.controlId];
        }
    }

    get errorMessage(): string
    {
        return this._errorMessageSignal();
    }

    private updateErrorMessage(): void
    {
        let $error: Record<string, boolean>;
        let msg = "";

        if (this.control?.errors && Object.keys(this.control.errors).length > 0)
            $error = this.control.errors;

        if ($error)
        {
            if ("required" in $error)
            {
                msg = `${this.Label} is Required`;
            }
            if ("maxlength" in $error)
            {
                msg = `${this.Label} cannot exceed ${this.MaxLength} characters`;
            }
            if ("pattern" in $error)
            {
                if (
                    this.$metadataInputTypeHelperService.isPhoneNumber(this.inputType) ||
                    this.$metadataInputTypeHelperService.isPhoneNumberWithExtension(this.inputType)
                )
                {
                    msg = "Please enter a valid phone number";
                }
                else
                {
                    msg = `${this.Label} does not match the expected format`;
                }
            }
            if ("precision" in $error)
            {
                msg = `${this.Label} cannot have more than ${this.precision} digits`;
            }
            if ("scale" in $error)
            {
                msg = `${this.Label} cannot have more than ${this.scale} decimal places`;
            }
            if ("minValue" in $error)
            {
                msg = `${this.Label} cannot have a value less than ${this.minValue}`;
            }
            if ("maxValue" in $error)
            {
                msg = `${this.Label} cannot have a value more than ${this.maxValue}`;
            }
            if ("date" in $error)
            {
                msg = `${this.Label} must be formatted as 'MM/dd/yyyy'`;
            }
            if ("compareTo" in $error)
            {
                if (this.errorMessages.length > 0)
                {
                    for (let i = 0, l = this.errorMessages.length; i < l; i++)
                    {
                        msg += this.errorMessages[i].value;
                        if (i + 1 < l)
                        {
                            msg += "\n";
                        }
                    }
                }
            }
        }

        if (msg)
        {
            this.propertyMetadata.$$formControlError = msg;
            this.propertyMetadata.$$touched = this.control?.touched;
        }
        else
            this.propertyMetadata.$$formControlError = undefined;

        if (this.onPanelGrid())
        {
            if (!this.propertyMetadata.$panelGridControlErrors)
                this.propertyMetadata.$panelGridControlErrors = {};
            if (this.propertyMetadata.$$formControlError)
                this.propertyMetadata.$panelGridControlErrors[this.controlId] = this.propertyMetadata.$$formControlError;
            else
                delete this.propertyMetadata.$panelGridControlErrors[this.controlId];
        }

        this._errorMessageSignal.set(msg);
    }

    get errorMessages(): ErrorMessage[]
    {
        return this._errorMessages;
    }
}
